/*
 * Bridge between redbox-ui and the Redbox Android / iOS apps (mobile/ in this repo).
 *
 * When the site runs inside the app, the app calls window.onNativePushToken(token, platform)
 * with its Firebase Cloud Messaging token. This module links that token to the logged-in
 * user (SESSION_ID cookie) on the server, so the backend can send push notifications,
 * and unlinks it when the user logs out.
 *
 * In a normal browser this module does nothing.
 */
import { getCookie } from './Communicator';
import { checkBiometric, initBiometric, onResumeBiometric } from './biometricLogin';

const REGISTER_URL = '/user/push/register';
const UNREGISTER_URL = '/user/push/unregister';
const STORAGE_KEY = 'redboxPushRegistration'; // "<token>|<session>" last registered
const CHECK_INTERVAL_MS = 5000;

export const isNativeApp = () => /RedboxApp\/(Android|iOS)/.test(navigator.userAgent);
export const nativePlatform = () => (/RedboxApp\/iOS/.test(navigator.userAgent) ? 'ios' : 'android');

let pushToken = null;
let platform = null;
let inFlight = false;

const readStored = () => {
    try { return localStorage.getItem(STORAGE_KEY) || ''; } catch (e) { return ''; }
};
const writeStored = (value) => {
    try {
        if (value) localStorage.setItem(STORAGE_KEY, value); else localStorage.removeItem(STORAGE_KEY);
    } catch (e) { /* storage unavailable */ }
};

const post = (url, body) =>
    fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });

/** Registers / unregisters the device when the token or the logged-in session changes. */
export const syncPushRegistration = async () => {
    if (!pushToken || inFlight) return;
    const session = getCookie('SESSION_ID') || '';
    const stored = readStored();
    const wanted = session ? `${pushToken}|${session}` : '';
    if (stored === wanted) return;

    inFlight = true;
    try {
        if (session) {
            const res = await post(REGISTER_URL, { session, token: pushToken, platform });
            if (res.ok) writeStored(wanted);
        } else if (stored) {
            // User logged out in the app: stop sending this user's notifications to this device.
            await post(UNREGISTER_URL, { token: stored.split('|')[0] });
            writeStored('');
        }
    } catch (e) {
        // Network error — retried on the next check.
    } finally {
        inFlight = false;
    }
};

export const initNativeApp = () => {
    if (!isNativeApp() || window.__redboxNativeInit) return;
    window.__redboxNativeInit = true;
    document.documentElement.classList.add('in-native-app'); // lets CSS hide "download the app" banners etc.

    // Called by the app after every page load and whenever the token rotates.
    window.onNativePushToken = (token, pf) => {
        if (!token) return;
        pushToken = token;
        platform = pf === 'ios' ? 'ios' : pf === 'android' ? 'android' : nativePlatform();
        syncPushRegistration();
    };

    // Login / logout happen without a page reload (SPA), so poll the session cookie.
    setInterval(() => {
        syncPushRegistration();
        checkBiometric();
    }, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            syncPushRegistration();
            onResumeBiometric();
        }
    });

    // Face ID / fingerprint login (biometricLogin.js).
    initBiometric(nativePlatform());

    // Ask the app for the token in case it was delivered before this script ran.
    try {
        if (window.AndroidBridge && window.AndroidBridge.requestPushToken) {
            window.AndroidBridge.requestPushToken();
        } else if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.nativeBridge) {
            window.webkit.messageHandlers.nativeBridge.postMessage({ action: 'requestPushToken' });
        }
    } catch (e) { /* bridge not available */ }
};
